import React, { useState, useRef, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components/macro';
import colors from 'styles/themes/colors';
import { ThemeProps } from 'types/styled';

const duration = 1250;

const LoadingAnimation = keyframes`
  0% {
    background-position: right bottom;
  }
  100% {
    background-position: left bottom;
  }
`;

const showAnimation = ({
  gradientColor,
  backgroundGradientColor,
}: {
  gradientColor: string;
  backgroundGradientColor: string;
}): any => css`
  background: linear-gradient(
      to right,
      ${backgroundGradientColor} 33.333%,
      ${gradientColor} 50%,
      ${backgroundGradientColor} 66.667%
    )
    repeat;
  background-size: 400% 100%;
  animation-name: ${LoadingAnimation};
  animation-duration: ${duration}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
`;

const notVisible = css`
  opacity: 0;
  visibility: hidden;
  animation: none;
`;

const AnimatingLoadingBar = styled.div<ThemeProps<'div'>>`
  width: 100%;
  height: 100%;
  position: relative;
  transition: ${({ theme }): string =>
    `all ${theme.transitionDuration} ${theme.timingFunction}`};

  ${({ showLoadingBar }): any => (showLoadingBar ? showAnimation : notVisible)};
`;

/**
 * LOADING ANIMATION BEHAVIOR
 *
 * As soon as the 'loading' prop becomes true, set a timeout that calls a
 * function after 'delay'. That function will start the animation. If loading
 * becomes false before that, clear the timeout.
 *
 * While the animation is running, if loading becomes false, it should finish
 * the current animation iteration and then stop.
 *
 * The code for this feature is inspired by Dan Abramov:
 * https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */

interface ILoadingBarProps {
  backgroundGradientColor?: string;
  className?: string;
  delay?: number;
  gradientColor?: string;
  loading: boolean;
}

/**
 * Show an animating loading bar.
 *
 */
const LoadingBar: React.FC<ILoadingBarProps> = (props) => {
  const {
    backgroundGradientColor = 'rgba(255, 255, 255, 0)',
    className,
    delay = 250,
    gradientColor = colors.AccentGreen,
    loading,
  } = props;
  const [showLoadingBar, setShowLoadingBar] = useState(false);

  /**
   * Here's the magic: Refs are always accessible by their latest value in other
   * hooks, without having to put them in the dependencies. They won't trigger
   * hooks to run.
   */
  const loop = useRef<any>();
  const timeout = useRef<any>();
  const tick = useRef<() => void | undefined>();

  /**  This hook is ran on every render, to update the tick ref. */
  useEffect(() => {
    tick.current = (): void => {
      /** If we aren't loading anymore */
      if (!loading) {
        /** Hide the loading bar */
        setShowLoadingBar(false);
        /** Stop running the loop */
        clearInterval(loop.current);
      }
    };
  });

  /** This hook is only executed when 'loading' changes. */
  useEffect(() => {
    // if we entered a loading state
    if (loading) {
      // run start after delay
      timeout.current = setTimeout(start, delay);
    }

    // if we exited a loading state
    if (!loading) {
      // forget about starting the animation
      clearTimeout(timeout.current);
    }

    /**
     * Start will display the animating loading bar and
     * set up an interval to run every 'duration'
     */
    function start(): void {
      /** Display the loading bar */
      setShowLoadingBar(true);

      /** Run the current value of the tick ref */
      function runEvery(): void {
        (tick as any)?.current();
      }

      /** Execute runEvery every 'duration' */
      loop.current = setInterval(runEvery, duration);
    }

    return function unmount(): void {
      clearTimeout(timeout.current);
      clearInterval(loop.current);
      setShowLoadingBar(false);
    };
  }, [delay, loading]);

  return (
    <AnimatingLoadingBar
      showLoadingBar={showLoadingBar}
      gradientColor={gradientColor}
      backgroundGradientColor={backgroundGradientColor}
      className={className}
    />
  );
};

export default LoadingBar;
